// Module 09 §8 — the widget registry, the inversion this module exists to make (ledger §A11).
//
// Legacy `CustomDashboardDataVisible.vue:130-137` hard-imported SIX other features to satisfy an
// 11-branch `v-else-if` chain: budget's `PerformanceOverviewArea`, 10's scorecard widget, 15's
// `SummaryInsights`/`SummaryAnomalies`, audience's `FunnelExploration`, 17's `Alerts`. That import
// list is the defect — a dashboard could not load if any one of those six failed to resolve, and
// every one of them dragged its whole feature into the dashboard chunk.
//
// Here the direction flips. A feature REGISTERS a lazy component for the kind it owns and 09
// imports none of 10/15/17/budget/audience. The loader stays a thunk so the feature's chunk is
// fetched when a dashboard actually renders that kind, never at boot.
//
// Registration is a boot-time concern: `app/bootstrap.ts` calls each feature's registrar (10's
// `registerScorecardDashboardWidget()`, 28's `registerAnomalyDashboardWidget()`, …) before the
// router mounts, exactly as it does for 07's bootstrap hydrators.
import type { AsyncComponentLoader } from 'vue'

import type { DashboardWidget, WidgetKind } from '../models/widget.types'

/**
 * The props every registered embed receives from `EmbedWidget`.
 *
 * This is the contract a registrant types its component against, so it is exported from the
 * barrel: a feature must be able to declare its props without importing 09's internals.
 * `widget.wire` carries the persisted bag, which is where kind-specific configuration lives
 * (10 reads `selectedKpi`, 28 reads nothing beyond the workspaces).
 */
export interface DashboardWidgetEmbedProps {
  widget: DashboardWidget
  /** The block's persisted name — legacy passed it as `:title` to all six embeds. */
  title: string
  /** The dashboard's workspace. `null` before the workspace resolves. */
  wsId: number | string | null
  /** Account-center multi-workspace set; empty for an ordinary dashboard. */
  accountWorkspaces: readonly (number | string)[]
  /** True for `viewer`/`viewerPlus` — the embed hides its own mutating affordances (§12). */
  readOnly: boolean
}

/**
 * Vue's own loader type, not the contract's `() => Promise<Component>`.
 *
 * `AsyncComponentLoader` resolves `Component | { default: Component }`, which is what makes the
 * documented call site — `registerDashboardWidget('anomalies', () => import('./AnomaliesWidget.vue'))`
 * — compile without the registrant unwrapping `.default` by hand.
 */
export type DashboardWidgetLoader = AsyncComponentLoader

const registry = new Map<WidgetKind, DashboardWidgetLoader>()

/**
 * Register the component that renders `kind`.
 *
 * Re-registering a kind REPLACES the previous loader (last writer wins), which is how the
 * migration's temporary shim registrations hand over to the real feature component without 09
 * changing. Returns an unregister that removes the loader only while it is still the active one,
 * so a stale teardown cannot delete a newer registration.
 */
export function registerDashboardWidget(
  kind: WidgetKind,
  loader: DashboardWidgetLoader,
): () => void {
  registry.set(kind, loader)
  return () => {
    if (registry.get(kind) === loader) registry.delete(kind)
  }
}

/** The loader for `kind`, or `null` when no feature has registered one. */
export function resolveDashboardWidget(kind: WidgetKind | null): DashboardWidgetLoader | null {
  return kind === null ? null : (registry.get(kind) ?? null)
}

/**
 * Whether `kind` has a registered renderer.
 *
 * An unregistered kind is not an error: the owning feature may be unmigrated, disabled for the
 * workspace, or simply not part of this build. `EmbedWidget` renders an inline placeholder and the
 * rest of the dashboard is unaffected (§14).
 */
export function hasDashboardWidget(kind: WidgetKind | null): boolean {
  return resolveDashboardWidget(kind) !== null
}

/** Test-only reset. Not exported from the barrel — a registry is boot state, not runtime state. */
export function clearDashboardWidgetRegistry(): void {
  registry.clear()
}
